import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Select, Checkbox, Button, Spin, message } from 'antd';
import { TagFilled, CloseOutlined } from '@ant-design/icons';
import { debounce } from '../../utils/PerformanceUtils';
import { GLOBAL_DEBOUNCE_TIME } from '../../utils/DataConstants';
import './AddLabelModal.less';

// Minimal label shape the modal needs — both WorkOrderLabel and the parts Label
// satisfy it. Kept independent of any one entity so the modal stays reusable.
export interface AddLabelModalLabel {
	id: number;
	label: string;
	color?: string;
}

export type LabelGlyphVariant = 'tag' | 'dot';

type SelectValue = number | string;

// Entity-agnostic Add Label modal. All entity-specific behavior (data fetching,
// assignment side effects, the edit drawer, the success/error toasts) is injected
// via props so both work orders and parts can share this UI. The wrappers below
// supply those bindings.
interface AddLabelModalProps {
	visible: boolean;
	onClose: () => void;
	entityId: number | string;
	existingLabels?: AddLabelModalLabel[];
	canModifyLabels: boolean;
	canApplyLabels: boolean;
	catalogue: AddLabelModalLabel[];
	catalogueFetching: boolean;
	fetchLabels: (search: string) => void;
	createLabel: (entity: { label: string; color: string }) => Promise<AddLabelModalLabel>;
	assignLabels: (entityId: number | string, ids: number[]) => Promise<any>;
	pickNewLabelColor: () => string;
	// Visual language of the entity: work order labels use the tag icon, part labels
	// use the colored dot.
	iconVariant?: LabelGlyphVariant;
	// Minimum trimmed length before the inline "+ Create New Label" row appears.
	minCreateLength?: number;
	// Assignment side-effect hooks — wrappers wire entity-specific behavior here.
	onBeforeAssign?: (entityId: number | string) => void;
	onAfterAssign?: (
		entityId: number | string,
		savedLabels: AddLabelModalLabel[],
		info: { wasEmpty: boolean }
	) => void;
	onAssignError?: (entityId: number | string) => void;
	onAssignSettled?: (entityId: number | string) => void;
}

const LabelGlyph: React.FC<{ color?: string; variant: LabelGlyphVariant }> = ({ color, variant }) =>
	variant === 'dot' ? (
		<span
			className="inline-block h-3 w-3 flex-shrink-0 rounded-full"
			style={{ backgroundColor: color }}
		/>
	) : (
		<TagFilled className="text-sm" style={{ color }} />
	);

export const AddLabelModal: React.FC<AddLabelModalProps> = ({
	visible,
	onClose,
	entityId,
	existingLabels = [],
	canModifyLabels,
	canApplyLabels,
	catalogue,
	catalogueFetching,
	fetchLabels,
	createLabel,
	assignLabels,
	pickNewLabelColor,
	iconVariant = 'tag',
	minCreateLength = 1,
	onBeforeAssign,
	onAfterAssign,
	onAssignError,
	onAssignSettled,
}) => {
	const existingIds = existingLabels.map((label) => label.id);
	const [selectedIds, setSelectedIds] = useState<SelectValue[]>(existingIds);
	const [searchText, setSearchText] = useState('');
	const [draftCreateLabelColor, setDraftCreateLabelColor] = useState<string | null>(null);
	// Labels created inline this session that may not be in the store catalogue yet.
	const [locallyCreated, setLocallyCreated] = useState<AddLabelModalLabel[]>([]);
	const [isCreatingLabel, setIsCreatingLabel] = useState(false);
	const [isAssigningLabels, setIsAssigningLabels] = useState(false);

	// `fetchLabels` is rebuilt every render by mergeProps, so hold the latest in a ref
	// and debounce a single stable function — otherwise the debounce timer resets each
	// keystroke and never fires.
	const fetchLabelsRef = useRef(fetchLabels);
	fetchLabelsRef.current = fetchLabels;
	// Latest intended search term. The debounced fetch reads this (not a captured arg) so a
	// timer that fires after the box was cleared/reset can't re-apply a stale query and wipe
	// the catalogue again (e.g. typing then clicking "Create New Label" within the debounce).
	const searchQueryRef = useRef('');
	const debouncedSearchFetch = useMemo(
		() => debounce(() => fetchLabelsRef.current(searchQueryRef.current), GLOBAL_DEBOUNCE_TIME),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	// Fetch + reset only when the modal opens. `existingLabels` and `catalogue` are
	// fresh array identities on every render, so they must NOT be effect deps — depending
	// on them re-runs the fetch every render and spins an infinite request loop.
	useEffect(() => {
		if (!visible) return;
		setSelectedIds(existingLabels.map((label) => label.id));
		setSearchText('');
		setDraftCreateLabelColor(null);
		setLocallyCreated([]);
		setIsCreatingLabel(false);
		setIsAssigningLabels(false);
		fetchLabels('');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visible, entityId]);

	// Working set = store catalogue (server-side search result) + any just-created labels
	// not yet reflected in it. Derived (not state) so it can't feed an update loop as
	// `catalogue` identity changes.
	const labels = useMemo(() => {
		const byId = new Map<SelectValue, AddLabelModalLabel>();
		catalogue.forEach((label) => byId.set(label.id, label));
		locallyCreated.forEach((label) => {
			if (!byId.has(label.id)) byId.set(label.id, label);
		});
		return Array.from(byId.values());
	}, [catalogue, locallyCreated]);

	// Resolves an id to a label for selected chips and save. Seeded with the entity's
	// already-assigned labels so chips render even when those labels are not in the
	// current catalogue page; current page + inline-created labels override.
	const labelById = useMemo(() => {
		const map = new Map<SelectValue, AddLabelModalLabel>();
		existingLabels.forEach((label) => map.set(label.id, label));
		labels.forEach((label) => map.set(label.id, label));
		return map;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [labels, existingLabels]);

	const baseOptions = useMemo(
		() =>
			labels.map((label) => ({
				value: label.id as SelectValue,
				label: label.label,
				color: label.color,
			})),
		[labels]
	);

	const trimmedSearch = searchText.trim();
	const matchExists =
		!!trimmedSearch &&
		labels.some((label) => label.label.toLowerCase() === trimmedSearch.toLowerCase());

	const showCreateNewLabelRow =
		trimmedSearch.length >= minCreateLength && !matchExists && canModifyLabels;

	useEffect(() => {
		if (showCreateNewLabelRow) {
			setDraftCreateLabelColor((current) => current || pickNewLabelColor());
			return;
		}
		setDraftCreateLabelColor(null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showCreateNewLabelRow]);

	// Server-side search replaces the dropdown collection with only the matches, so clearing
	// the box must refetch the full set — otherwise the dropdown stays stuck on the last
	// search's (often empty) result set. Skip the refetch when nothing was searched so
	// selecting/closing without a query costs no request.
	const clearSearchInput = () => {
		setSearchText('');
		if (searchQueryRef.current) {
			searchQueryRef.current = '';
			fetchLabels('');
		}
	};

	// Server-side search: keep the box responsive, debounce the actual request.
	const handleSearch = (value: string) => {
		setSearchText(value);
		searchQueryRef.current = value.trim();
		debouncedSearchFetch();
	};

	const handleDropdownVisibleChange = (open: boolean) => {
		if (!open) {
			clearSearchInput();
		}
	};

	const handleCreateLabel = () => {
		if (!showCreateNewLabelRow || isCreatingLabel) return;
		const color = draftCreateLabelColor || pickNewLabelColor();
		const optimisticName = trimmedSearch;
		// Clear + refetch the full catalogue so the labels the "create" search filtered out
		// reappear alongside the newly created one (which also shows via `locallyCreated`).
		clearSearchInput();
		setIsCreatingLabel(true);
		createLabel({ label: optimisticName, color })
			.then((created) => {
				if (!created) return;
				setLocallyCreated((current) =>
					current.some((label) => label.id === created.id) ? current : [...current, created]
				);
				setSelectedIds((current) =>
					current.includes(created.id) ? current : [...current, created.id]
				);
			})
			.catch(() => {
				message.error(`Failed to create label "${optimisticName}".`);
				handleSearch(optimisticName);
				setDraftCreateLabelColor(color);
			})
			.finally(() => {
				setIsCreatingLabel(false);
			});
	};

	const renderOption = (option: { value: SelectValue; label: string; color: string }) => (
		<div className="inline-flex w-full items-center gap-2 py-0.5">
			<Checkbox checked={selectedIds.includes(option.value)} />
			<LabelGlyph color={option.color} variant={iconVariant} />
			<span>{option.label}</span>
		</div>
	);

	const tagRender = ({ value, onClose: onTagClose }: any) => {
		const label = labelById.get(value);
		if (!label) return <span />;
		return (
			<span className="mr-1 inline-flex h-[22px] items-center gap-1 rounded-[2px] bg-grey-3 px-2">
				<LabelGlyph color={label.color} variant={iconVariant} />
				<span className="footnoteregular text-grey-10">{label.label}</span>
				{canApplyLabels ? (
					<CloseOutlined
						onClick={(event) => {
							event.stopPropagation();
							onTagClose();
						}}
						className="cursor-pointer text-[10px] text-grey-7"
					/>
				) : null}
			</span>
		);
	};

	const hasFilteredMatches = trimmedSearch
		? labels.some((label) => label.label.toLowerCase().includes(trimmedSearch.toLowerCase()))
		: labels.length > 0;

	const dropdownRender = (menu: React.ReactNode) => (
		<>
			{hasFilteredMatches ? menu : null}
			{showCreateNewLabelRow && draftCreateLabelColor ? (
				<div
					onMouseDown={(event) => event.preventDefault()}
					onClick={handleCreateLabel}
					className={`bodyregular flex cursor-pointer items-center gap-1.5 px-3 py-2 text-character-primary${
						hasFilteredMatches ? ' border-t border-solid border-grey-4' : ''
					}`}
				>
					<span>+ Create New Label:</span>
					<span className="text-character-secondary">“</span>
					<LabelGlyph color={draftCreateLabelColor} variant={iconVariant} />
					<span>{trimmedSearch}</span>
					<span className="text-character-secondary">”</span>
				</div>
			) : null}
		</>
	);

	const handleSave = () => {
		if (isAssigningLabels || isCreatingLabel) return;
		const wasEmpty = existingIds.length === 0;
		const savedLabels = (selectedIds as SelectValue[])
			.map((labelId) => labelById.get(labelId))
			.filter((label): label is AddLabelModalLabel => !!label);
		const numericIds = savedLabels.map((label) => label.id);
		setIsAssigningLabels(true);
		if (onBeforeAssign) onBeforeAssign(entityId);
		assignLabels(entityId, numericIds)
			.then(() => {
				if (onAfterAssign) onAfterAssign(entityId, savedLabels, { wasEmpty });
				onClose();
			})
			.catch(() => {
				if (onAssignError) onAssignError(entityId);
			})
			.finally(() => {
				setIsAssigningLabels(false);
				if (onAssignSettled) onAssignSettled(entityId);
			});
	};

	// Cancel/X with unsaved assignment changes prompts a discard confirm; a clean modal closes
	// immediately. Outside-click and ESC never reach here (maskClosable/keyboard are off), so they
	// can't silently discard — they only dismiss the open Select dropdown.
	const attemptClose = () => {
		if (isAssigningLabels || isCreatingLabel) return;
		const selectedSet = new Set((selectedIds as SelectValue[]).map(Number));
		const dirty =
			selectedSet.size !== existingIds.length || existingIds.some((id) => !selectedSet.has(id));
		if (!dirty) {
			onClose();
			return;
		}
		Modal.confirm({
			title: 'Are you sure?',
			content: 'Changes will be discarded.',
			okText: 'Discard',
			cancelText: 'Cancel',
			okButtonProps: { danger: true },
			onOk: onClose,
		});
	};

	return (
		<>
			<Modal
				title={canApplyLabels ? 'Add Label' : 'Labels'}
				visible={visible}
				onCancel={isAssigningLabels ? undefined : attemptClose}
				closable={!isAssigningLabels}
				maskClosable={false}
				keyboard={false}
				width={572}
				destroyOnClose
				footer={
					<div className="flex items-center justify-end">
						<div className="flex gap-2">
							<Button
								onClick={attemptClose}
								disabled={isAssigningLabels || isCreatingLabel}
								className="!rounded-[2px]"
							>
								{canApplyLabels ? 'CANCEL' : 'CLOSE'}
							</Button>
							{canApplyLabels ? (
								<Button
									type="primary"
									onClick={handleSave}
									disabled={isCreatingLabel || isAssigningLabels}
									loading={isCreatingLabel || isAssigningLabels}
									className="!rounded-[2px]"
								>
									SAVE
								</Button>
							) : null}
						</div>
					</div>
				}
			>
				<div
					className={`add-label-select-wrapper relative${
						selectedIds.length > 0 ? ' has-selection' : ''
					}${selectedIds.length > 0 && !searchText ? ' show-placeholder-on-search-row' : ''}`}
				>
					<Select
						className="add-label-select w-full"
						mode="multiple"
						disabled={!canApplyLabels}
						suffixIcon={null}
						dropdownMatchSelectWidth
						listHeight={256}
						value={selectedIds as any}
						onChange={(value) => {
							setSelectedIds(value as SelectValue[]);
							clearSearchInput();
						}}
						onSearch={handleSearch}
						searchValue={searchText}
						onDropdownVisibleChange={handleDropdownVisibleChange}
						placeholder="search or begin typing to create new label"
						optionLabelProp="label"
						menuItemSelectedIcon={null}
						tagRender={tagRender}
						dropdownRender={dropdownRender}
						notFoundContent={
							catalogueFetching ? (
								<div className="flex justify-center py-2">
									<Spin size="small" />
								</div>
							) : (
								<span className="hidden" />
							)
						}
						filterOption={false}
					>
						{baseOptions.map((option) => (
							<Select.Option key={option.value} value={option.value} label={option.label}>
								{renderOption(option)}
							</Select.Option>
						))}
					</Select>
					{selectedIds.length > 0 && !searchText && canApplyLabels ? (
						<span className="add-label-select-placeholder bodyregular pointer-events-none absolute bottom-[7px] left-3 right-3 overflow-hidden text-ellipsis whitespace-nowrap text-character-disabled">
							search or begin typing to create new label
						</span>
					) : null}
				</div>
			</Modal>
		</>
	);
};
